// Mobile GM shell — replaces ZenGM's chrome (navbar/sidebar/title bar/footer) with the
// design's dark canvas + bottom tab nav (Roster · Home · Stats, Home centered). The current
// view renders inside; screens supply their own headers/content.

import { type ReactNode, useEffect, useState } from "react";
import { useLocal, localActions } from "../../util/local.ts";
import { toWorker } from "../../util/toWorker.ts";
import { realtimeUpdate } from "../../util/realtimeUpdate.ts";
import { confirm } from "../../util/confirm.tsx";
import {
	setPlayoffsRound,
	usePlayoffsRound,
} from "../../util/playoffsRound.ts";
import { PHASE } from "../../../common/constants.ts";
import { challengeInviteText } from "../../util/challengeUi.ts";

// Home is "home to the active state": tapping it lands on whatever the franchise is doing
// right now. In-season phases fall through to the dashboard (sub ""); the offseason phases
// each own a screen. Free agency is no longer a standalone tab — during FA, Home *is* the
// free agents screen. Anytime signing still lives under Roster › Free Agents.
const HOME_PHASE_SUB: Record<number, string> = {
	[PHASE.PLAYOFFS]: "/playoffs",
	[PHASE.DRAFT_LOTTERY]: "/draft_lottery",
	[PHASE.DRAFT]: "/draft",
	[PHASE.RESIGN_PLAYERS]: "/negotiation",
	[PHASE.FREE_AGENCY]: "/free_agents",
};

// `root: true` navigates out to the Dynasty league selector instead of an in-league sub-route.
const TABS: {
	label: string;
	sub?: string;
	home?: boolean;
	root?: boolean;
	icon: string;
}[] = [
	{
		label: "Home",
		home: true,
		icon: "M4 11 12 4l8 7v8a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z",
	},
	{
		label: "Roster",
		sub: "/roster",
		icon: "M7 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6m10 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6M2 20c0-3 2.5-5 5-5s5 2 5 5m0 0c0-3 2.5-5 5-5s5 2 5 5",
	},
	{
		label: "Stats",
		sub: "/player_stats",
		icon: "M4 20V10m5 10V4m5 16v-7m5 7V8",
	},
	{
		label: "Settings",
		root: true,
		icon: "M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6",
	},
];

export const GmShell = ({
	children,
	inLeague,
}: {
	children: ReactNode;
	inLeague: boolean;
}) => {
	const {
		lid,
		phase,
		season,
		playMenuOptions,
		tradeDeadlinePrompt,
		gameSimInProgress,
	} = useLocal([
		"lid",
		"phase",
		"season",
		"playMenuOptions",
		"tradeDeadlinePrompt",
		"gameSimInProgress",
	]);

	// The sim halts at the trade deadline (see core/game/play.ts) and sets tradeDeadlinePrompt to
	// the days still queued. Prompt the user: make a trade (deadline hasn't passed yet, so trades
	// are still allowed) or keep simulating (resume the original request past the deadline).
	useEffect(() => {
		if (tradeDeadlinePrompt === undefined) {
			return;
		}
		const numDays = tradeDeadlinePrompt;
		// Clear immediately so the prompt fires exactly once.
		localActions.update({ tradeDeadlinePrompt: undefined });
		(async () => {
			const makeTrade = await confirm(
				"You've reached the trade deadline. Make a trade before it passes, or keep simulating?",
				{ okText: "Make a Trade", cancelText: "Keep Simulating" },
			);
			if (makeTrade) {
				await toWorker("playMenu", "acknowledgeTradeDeadline", undefined);
				realtimeUpdate([], `/l/${lid}/trade`);
			} else {
				await toWorker("playMenu", "resumeAfterTradeDeadline", numDays);
			}
		})();
	}, [tradeDeadlinePrompt, lid]);
	const pathname =
		typeof document !== "undefined" ? document.location.pathname : "";

	// Phase chip text + phase-aware advance action, computed worker-side. Refetched whenever
	// the play menu changes (after each sim/pick) or the route changes.
	type ShellInfo = {
		chip?: string;
		monthAdvance?: { label: string; id: string; confirm?: string };
		linkAdvance?: { label: string; sub: string };
		draft?: { lottery: boolean; usersTurn: boolean; started: boolean };
		playoffs?: { currentRound: number; roundName: string; simId: string };
		challenge?: {
			status: "active" | "complete";
			code: string;
			playerId: string;
			record: string;
			grade?: string;
			gradeLabel: string;
			gmScore?: number;
			endsLabel: string;
		};
	};
	const [info, setInfo] = useState<ShellInfo>({});
	useEffect(() => {
		let cancelled = false;
		(async () => {
			const res = await toWorker("main", "getShellInfo", undefined);
			if (!cancelled) {
				setInfo((res as any) ?? {});
				// The real bar state (Stop / next action) is now shown — release the tap-pending hold.
				setPending(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [playMenuOptions, phase, pathname]);

	// Branded roster-size gate: set when the user tries to sim with too few/many players.
	type RosterIssue = {
		kind: "over" | "under";
		count: number;
		min: number;
		max: number;
	};
	const [rosterIssue, setRosterIssue] = useState<RosterIssue | null>(null);

	// Instant tap feedback: firing a sim is a worker round-trip (~250ms) before the bar can flip
	// to "Stop", so the button looked unresponsive / could be double-tapped. `pending` disables it
	// and shows a working label the moment it's tapped, until the next getShellInfo settles it.
	const [pending, setPending] = useState(false);
	// A trade-deadline (or other) advance awaiting the user's confirmation.
	const [confirmAdv, setConfirmAdv] = useState<Adv | null>(null);

	// Executive Challenge status sheet (opened from the header badge) + the
	// transient "Copied ✓" state on its share button.
	const [challengeSheet, setChallengeSheet] = useState(false);
	// The phase-chip sheet: on-demand Progress Report entry point.
	const [chipSheet, setChipSheet] = useState(false);
	const [codeCopied, setCodeCopied] = useState(false);
	const challenge = info.challenge;

	const onBadge = () => {
		if (!challenge) {
			return;
		}
		if (challenge.status === "complete" && lid !== undefined) {
			realtimeUpdate([], `/l/${lid}/challenge_result`);
		} else {
			setChallengeSheet(true);
		}
	};

	const copyChallengeCode = async () => {
		if (!challenge) {
			return;
		}
		try {
			await navigator.clipboard.writeText(challengeInviteText(challenge.code));
			setCodeCopied(true);
			setTimeout(() => setCodeCopied(false), 2000);
		} catch {
			// Clipboard unavailable — the code is on screen anyway.
		}
	};

	// Playoffs advance is a two-step-per-round flow. `viewedRound` (shared with GmPlayoffs) is the
	// round the user is currently looking at; when the live currentRound moves past it (a round was
	// just simmed) the bar shows "Continue to {round}" instead of the next "Sim {round}".
	const poStore = usePlayoffsRound();
	const poKey = `po-${season}`;
	const poCurrent = info.playoffs?.currentRound;
	// Initialize / reset the shared round when a new postseason appears (key changes).
	useEffect(() => {
		if (info.playoffs && poStore.key !== poKey) {
			setPlayoffsRound(info.playoffs.currentRound, poKey);
		}
	}, [info.playoffs, poKey, poStore.key]);
	const viewedRound = poStore.key === poKey ? poStore.round : (poCurrent ?? 0);

	const primary = playMenuOptions[0];
	// Exact match only — pathname.includes("/l/1/draft") would also match /draft_scouting,
	// /draft_lottery, etc., falsely reading as "on the draft board".
	const onDraftScreen =
		lid !== undefined &&
		(pathname === `/l/${lid}/draft` || pathname === `/l/${lid}/draft/`);

	// The advance/sim bar is hidden on the browsing screens (Stats + all three Roster tabs:
	// My Team, Free Agents, Trades) — those are for managing, not advancing the calendar.
	const onStatsOrRoster =
		lid !== undefined &&
		(pathname.startsWith(`/l/${lid}/player_stats`) ||
			pathname.startsWith(`/l/${lid}/progress_report`) ||
			pathname.startsWith(`/l/${lid}/team_stats_m`) ||
			pathname.startsWith(`/l/${lid}/roster`) ||
			pathname.startsWith(`/l/${lid}/signings`) ||
			pathname.startsWith(`/l/${lid}/trade`));

	// The player card (tap a player from any roster) is a read-only detail screen, not a place to
	// advance the calendar — hide the bar there too. Trailing slash keeps it off /player_stats etc.
	const onPlayerCard =
		lid !== undefined && pathname.startsWith(`/l/${lid}/player/`);

	// The season-preview ("Offseason report") screen is shown once, right after the season starts.
	// Its advance is a plain "Advance to Regular Season" that just drops the user into the in-season
	// dashboard — no calendar sim yet.
	const onSeasonPreview =
		lid !== undefined && pathname.startsWith(`/l/${lid}/season_preview`);

	// On the draft-lottery screen, GmLottery owns the footer CTA (Start Lottery → reveal →
	// Continue to Draft). Hide the shell's advance bar there so the two don't stack.
	const onDraftLottery =
		lid !== undefined && pathname.startsWith(`/l/${lid}/draft_lottery`);

	// Build the advance control. Precedence: Stop (a sim is running) > draft-specific states >
	// calendar-month (regular season) > the default play-menu option.
	type Adv = {
		label: string;
		id?: string;
		url?: string;
		route?: string;
		disabled?: boolean;
		continueToRound?: number;
		// When set, tapping the advance bar opens a confirm dialog with this text before firing.
		confirm?: string;
	};
	let advance: Adv | undefined;
	if (challenge?.status === "complete") {
		// A completed challenge save is read-only: the only advance ever offered
		// is the link to the locked result (below, via linkAdvance) — never a sim.
		// On the result page itself, no bar at all. (The worker's playMenu is
		// also hard-gated; this just keeps the dead control from rendering.)
		if (
			info.linkAdvance &&
			lid !== undefined &&
			!pathname.startsWith(`/l/${lid}${info.linkAdvance.sub}`)
		) {
			advance = {
				label: info.linkAdvance.label,
				route: `/l/${lid}${info.linkAdvance.sub}`,
			};
		}
	} else if (primary?.id === "stop") {
		advance = primary;
	} else if (
		info.linkAdvance &&
		lid !== undefined &&
		!pathname.startsWith(`/l/${lid}${info.linkAdvance.sub}`)
	) {
		// A plain "go to this screen" advance (e.g. "See the Year in Review" after the finals).
		advance = {
			label: info.linkAdvance.label,
			route: `/l/${lid}${info.linkAdvance.sub}`,
		};
	} else if (onSeasonPreview) {
		advance = { label: "Advance to Regular Season", route: `/l/${lid}` };
	} else if (info.playoffs) {
		if (viewedRound < info.playoffs.currentRound) {
			// A newer round exists — pause on the just-finished round, then continue to it.
			advance = {
				label: `Continue to ${info.playoffs.roundName}`,
				continueToRound: info.playoffs.currentRound,
			};
		} else {
			// On the current round — sim it (stays on the playoffs screen).
			advance = {
				label: `Sim ${info.playoffs.roundName}`,
				id: info.playoffs.simId,
			};
		}
	} else if (info.draft) {
		if (info.draft.lottery) {
			// DRAFT_LOTTERY phase: the lottery reveal screen must load before the draft board.
			// Route there first; only once the user is on it does "Start Draft" (untilDraft, which
			// then auto-redirects to the board) appear — so the lottery can't be skipped.
			const onLottery =
				lid !== undefined && pathname.startsWith(`/l/${lid}/draft_lottery`);
			advance = onLottery
				? { label: "Start Draft", id: "untilDraft" }
				: { label: "View draft lottery", route: `/l/${lid}/draft_lottery` };
		} else if (!onDraftScreen) {
			advance = { label: "Back to Draft", route: `/l/${lid}/draft` };
		} else if (info.draft.usersTurn) {
			// Your pick — must draft or trade the pick; no auto-advance.
			advance = { label: "Your pick — draft or trade", disabled: true };
		} else {
			// On the board, between picks — sim to the user's next selection. (The draft was
			// already begun by the lottery "Start Draft"; started distinguishes first-pick only.)
			advance = { label: "Sim to my pick", id: "untilYourNextPick" };
		}
	} else if (info.monthAdvance) {
		advance = info.monthAdvance;
	} else if (primary) {
		advance = primary;
	}

	// While a sim is actually running, disable the advance bar so it can't be tapped again (e.g.
	// during a multi-round playoff sim, before the bar has flipped to "Stop"). The Stop button
	// itself must stay enabled so the user can still halt the sim.
	const simBlocked = gameSimInProgress && advance?.id !== "stop";

	// On the playoffs the action screen has no bottom-nav tab, so route there when advancing.
	const phaseRoute = phase === PHASE.PLAYOFFS ? "playoffs" : undefined;

	// Sims that actually play games happen only in these phases; that's where the engine enforces
	// roster size, so that's where we pre-check before firing the sim.
	const gameplayPhase =
		phase === PHASE.REGULAR_SEASON ||
		phase === PHASE.AFTER_TRADE_DEADLINE ||
		phase === PHASE.PLAYOFFS;

	const onAdvance = async () => {
		if (!advance || advance.disabled || pending || simBlocked) {
			return;
		}
		// Irreversible steps (e.g. simming past the trade deadline) open a confirm dialog first;
		// the dialog's Continue button calls doAdvance directly.
		if (advance.confirm) {
			setConfirmAdv(advance);
			return;
		}
		await doAdvance(advance);
	};

	const doAdvance = async (advance: Adv) => {
		if (advance.continueToRound !== undefined) {
			// "Continue to Round N" — catch the viewed round up to the live one, stay on /playoffs.
			setPlayoffsRound(advance.continueToRound, poKey);
			if (lid !== undefined && !pathname.includes("/playoffs")) {
				realtimeUpdate([], `/l/${lid}/playoffs`);
			}
			return;
		}
		if (advance.route) {
			realtimeUpdate([], advance.route);
			return;
		}
		if (advance.url) {
			realtimeUpdate([], advance.url);
			return;
		}
		if (advance.id) {
			// Instant feedback — flip the bar to a disabled "working" state before the worker
			// round-trip so the tap registers immediately and can't be double-fired.
			setPending(true);
			// Block a game sim with an illegal roster and show a branded message instead of the
			// engine's plain error.
			if (gameplayPhase) {
				const issue = (await toWorker(
					"main",
					"getRosterSizeIssue",
					undefined,
				)) as RosterIssue | null;
				if (issue) {
					setRosterIssue(issue);
					setPending(false);
					return;
				}
			}
			toWorker("playMenu", advance.id as any, undefined);
		}
		if (
			phaseRoute &&
			lid !== undefined &&
			!pathname.includes(`/${phaseRoute}`)
		) {
			realtimeUpdate([], `/l/${lid}/${phaseRoute}`);
		}
	};

	return (
		<div className="gm-shell">
			<style>{shellStyles}</style>

			{inLeague && (info.chip || challenge) ? (
				<div className="gm-header-right">
					{challenge ? (
						<button
							type="button"
							className={`gm-ch-badge ${challenge.status === "complete" ? "done" : ""}`}
							onClick={onBadge}
						>
							{challenge.status === "complete" ? (
								<>
									✓<span className="gm-ch-badge-txt">COMPLETE</span>
								</>
							) : (
								<>
									<span className="gm-ch-badge-dot" />🏆
									<span className="gm-ch-badge-txt">CHALLENGE</span>
								</>
							)}
						</button>
					) : null}
					{info.chip ? (
						<button
							type="button"
							className="gm-chip gm-chip-btn"
							onClick={() => setChipSheet(true)}
						>
							{info.chip}
						</button>
					) : null}
				</div>
			) : null}

			{chipSheet ? (
				<div className="gm-roster-backdrop" onClick={() => setChipSheet(false)}>
					<div className="gm-roster-sheet" onClick={(e) => e.stopPropagation()}>
						<div className="gm-roster-badge under">📊</div>
						<h3 className="gm-roster-title gm-ch-sheet-title">
							{(info.chip ?? "SEASON").toUpperCase()}
						</h3>
						<p className="gm-roster-sub">
							Grade the season so far — your projected report card and the front
							office moves behind it, ready to share.
						</p>
						<button
							type="button"
							className="gm-roster-cta"
							onClick={() => {
								setChipSheet(false);
								if (lid !== undefined) {
									realtimeUpdate([], `/l/${lid}/progress_report`);
								}
							}}
						>
							📸 Create Progress Report
						</button>
						<button
							type="button"
							className="gm-roster-dismiss"
							onClick={() => setChipSheet(false)}
						>
							Close
						</button>
					</div>
				</div>
			) : null}

			{challengeSheet && challenge ? (
				<div
					className="gm-roster-backdrop"
					onClick={() => setChallengeSheet(false)}
				>
					<div className="gm-roster-sheet" onClick={(e) => e.stopPropagation()}>
						<div className="gm-roster-badge under">🏆</div>
						<h3 className="gm-roster-title gm-ch-sheet-title">
							SUMMER 2026 EXECUTIVE CHALLENGE
						</h3>
						<p className="gm-roster-sub">
							The world is frozen — only your moves count.
						</p>
						<div className="gm-ch-rows">
							<div className="gm-ch-row">
								<span>RECORD</span>
								<b>{challenge.record}</b>
							</div>
							<div className="gm-ch-row">
								<span>{challenge.gradeLabel.toUpperCase()}</span>
								<b className="teal">{challenge.grade ?? "—"}</b>
							</div>
							<div className="gm-ch-row">
								<span>ENDS</span>
								<b>{challenge.endsLabel}</b>
							</div>
						</div>
						<div className="gm-ch-invite">
							<div className="gm-ch-invite-code">
								<span>INVITE CODE</span>
								<b>{challenge.code}</b>
							</div>
							<button
								type="button"
								className="gm-ch-share"
								onClick={copyChallengeCode}
							>
								{codeCopied ? "Copied ✓" : "Share"}
							</button>
						</div>
						{challenge.status === "complete" ? (
							<button
								type="button"
								className="gm-roster-cta"
								onClick={() => {
									setChallengeSheet(false);
									if (lid !== undefined) {
										realtimeUpdate([], `/l/${lid}/challenge_result`);
									}
								}}
							>
								View Result →
							</button>
						) : (
							<button
								type="button"
								className="gm-ch-gotit"
								onClick={() => setChallengeSheet(false)}
							>
								Got it
							</button>
						)}
					</div>
				</div>
			) : null}

			<div className="gm-shell-content">{children}</div>

			{inLeague &&
			advance &&
			!onStatsOrRoster &&
			!onDraftLottery &&
			!onPlayerCard ? (
				<button
					className="gm-advance"
					onClick={onAdvance}
					disabled={advance.disabled || pending || simBlocked}
				>
					{pending || simBlocked ? "Working…" : advance.label}
					{advance.disabled || pending || simBlocked ? "" : " ▸"}
				</button>
			) : null}

			{rosterIssue ? (
				<div
					className="gm-roster-backdrop"
					onClick={() => setRosterIssue(null)}
				>
					<div className="gm-roster-sheet" onClick={(e) => e.stopPropagation()}>
						<div
							className={`gm-roster-badge ${rosterIssue.kind === "over" ? "over" : "under"}`}
						>
							{rosterIssue.kind === "over" ? "▲" : "▼"}
						</div>
						<h3 className="gm-roster-title">
							{rosterIssue.kind === "over"
								? "Roster too large"
								: "Roster too small"}
						</h3>
						<p className="gm-roster-sub">
							You have <b>{rosterIssue.count}</b>{" "}
							{rosterIssue.count === 1 ? "player" : "players"}.{" "}
							{rosterIssue.kind === "over"
								? `You can carry at most ${rosterIssue.max} — release or trade players before you sim.`
								: `You need at least ${rosterIssue.min} — sign free agents before you sim.`}
						</p>
						<button
							type="button"
							className="gm-roster-cta"
							onClick={() => {
								setRosterIssue(null);
								if (lid !== undefined) {
									realtimeUpdate(
										[],
										rosterIssue.kind === "over"
											? `/l/${lid}/roster`
											: `/l/${lid}/free_agents`,
									);
								}
							}}
						>
							{rosterIssue.kind === "over" ? "Go to roster" : "Sign players"}
						</button>
						<button
							type="button"
							className="gm-roster-dismiss"
							onClick={() => setRosterIssue(null)}
						>
							Dismiss
						</button>
					</div>
				</div>
			) : null}

			{confirmAdv ? (
				<div className="gm-roster-backdrop" onClick={() => setConfirmAdv(null)}>
					<div className="gm-roster-sheet" onClick={(e) => e.stopPropagation()}>
						<div className="gm-roster-badge over">!</div>
						<h3 className="gm-roster-title">Trade deadline</h3>
						<p className="gm-roster-sub">{confirmAdv.confirm}</p>
						<button
							type="button"
							className="gm-roster-cta"
							onClick={() => {
								const a = confirmAdv;
								setConfirmAdv(null);
								if (a) {
									doAdvance(a);
								}
							}}
						>
							Sim past deadline
						</button>
						<button
							type="button"
							className="gm-roster-dismiss"
							onClick={() => setConfirmAdv(null)}
						>
							Cancel
						</button>
					</div>
				</div>
			) : null}

			{inLeague && lid !== undefined ? (
				<nav className="gm-nav">
					{TABS.map((tab) => {
						// Root tabs (Settings) leave the league for the Dynasty selector.
						if (tab.root) {
							return (
								<a
									key={tab.label}
									href="/dynasty"
									className="gm-nav-item"
									onClick={(e) => {
										e.preventDefault();
										realtimeUpdate([], "/dynasty");
									}}
								>
									<svg
										viewBox="0 0 24 24"
										width="22"
										height="22"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d={tab.icon} />
									</svg>
									<span>{tab.label}</span>
								</a>
							);
						}
						const sub = tab.home
							? (HOME_PHASE_SUB[phase] ?? "")
							: (tab.sub ?? "");
						const href = `/l/${lid}${sub}`;
						let active =
							sub === ""
								? pathname === `/l/${lid}` || pathname === `/l/${lid}/`
								: pathname.startsWith(href);
						// Roster's sub-screens (Free Agents → /signings, Trades → /trade) keep the
						// Roster tab lit even though they live at their own routes.
						if (
							tab.label === "Roster" &&
							(pathname.startsWith(`/l/${lid}/signings`) ||
								pathname.startsWith(`/l/${lid}/trade`))
						) {
							active = true;
						}
						return (
							<a
								key={tab.label}
								href={href}
								className={`gm-nav-item ${active ? "active" : ""}`}
							>
								<svg
									viewBox="0 0 24 24"
									width="22"
									height="22"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d={tab.icon} />
								</svg>
								<span>{tab.label}</span>
							</a>
						);
					})}
				</nav>
			) : null}
		</div>
	);
};

const shellStyles = `
:root, body { background:#121212 !important; }
#content, #actual-content { background:#121212; }
.gm-shell { min-height:100vh; background:#121212; color:#fff;
  font-family: Montserrat, system-ui, sans-serif; }
.gm-shell-content { max-width:760px; margin:0 auto; padding-bottom:150px; }
.gm-header-right { position:fixed; top:12px; right:12px; z-index:1002; display:flex;
  align-items:center; gap:8px; }
.gm-chip-btn { cursor:pointer; font-family:Montserrat,system-ui,sans-serif; }
.gm-chip { background:rgba(24,24,24,.85);
  border:1px solid #2a2a2a; border-radius:16px; padding:6px 13px; font-size:12px; font-weight:700;
  color:#cfe0ec; backdrop-filter:blur(8px); max-width:50vw; white-space:nowrap; overflow:hidden;
  text-overflow:ellipsis; }
.gm-gear { width:36px; height:36px; border-radius:50%; background:rgba(255,255,255,.08);
  border:1px solid rgba(255,255,255,.12); color:#cdd9e2; display:flex; align-items:center;
  justify-content:center; flex:none; text-decoration:none; backdrop-filter:blur(8px); }
.gm-gear:hover { color:#fff; background:rgba(255,255,255,.14); }
.gm-advance { position:fixed; left:50%; transform:translateX(-50%); bottom:70px; z-index:1001;
  width:calc(100% - 24px); max-width:736px; border:none; border-radius:28px; background:#18E0A8;
  color:#0a0a0a; font-family:Montserrat,system-ui,sans-serif; font-weight:800; font-size:16px;
  padding:15px; cursor:pointer; box-shadow:0 10px 26px rgba(24,224,168,.3); }
.gm-advance:disabled { background:#232323; color:#7f8b93; box-shadow:none; cursor:default; }
.gm-roster-backdrop { position:fixed; inset:0; z-index:2000; background:rgba(0,0,0,.66);
  display:flex; align-items:flex-end; justify-content:center; animation:gmRosterFade .2s ease both; }
.gm-roster-sheet { width:100%; max-width:520px; background:#181818; border:1px solid #2a2a2a;
  border-radius:22px 22px 0 0; padding:22px 20px calc(20px + env(safe-area-inset-bottom));
  text-align:center; animation:gmRosterUp .26s cubic-bezier(.2,.9,.3,1.1) both; }
.gm-roster-badge { width:52px; height:52px; border-radius:50%; margin:0 auto 12px; display:flex;
  align-items:center; justify-content:center; font-size:22px; font-weight:900; }
.gm-roster-badge.over { background:rgba(226,80,78,.16); color:#E2504E; }
.gm-roster-badge.under { background:rgba(24,224,168,.16); color:#18E0A8; }
.gm-roster-title { font-size:20px; font-weight:900; letter-spacing:-.3px; margin:0 0 8px; }
.gm-roster-sub { font-size:13px; font-weight:600; color:#9aa4ac; line-height:1.5; margin:0 auto 18px; max-width:340px; }
.gm-roster-sub b { color:#fff; font-weight:800; }
.gm-roster-cta { width:100%; border:none; border-radius:24px; background:#18E0A8; color:#053a2c;
  font-family:Montserrat,system-ui,sans-serif; font-weight:900; font-size:15px; padding:14px; cursor:pointer; }
.gm-roster-dismiss { width:100%; margin-top:9px; border:none; background:none; color:#8a8a8a;
  font-family:Montserrat,system-ui,sans-serif; font-weight:800; font-size:14px; padding:10px; cursor:pointer; }
@keyframes gmRosterFade { from { opacity:0; } to { opacity:1; } }
@keyframes gmRosterUp { from { transform:translateY(30px); } to { transform:none; } }
/* Executive Challenge header badge + status sheet */
.gm-ch-badge { display:flex; align-items:center; gap:6px; height:30px; padding:0 11px;
  border-radius:15px; background:rgba(24,24,24,.85); border:1px solid #2a2a2a; color:#18E0A8;
  font-size:11px; font-weight:800; letter-spacing:.3px; backdrop-filter:blur(8px); cursor:pointer;
  font-family:Montserrat,system-ui,sans-serif; white-space:nowrap; }
.gm-ch-badge.done { background:rgba(24,224,168,.16); border-color:rgba(24,224,168,.45); }
.gm-ch-badge-dot { width:6px; height:6px; border-radius:50%; background:#18E0A8;
  box-shadow:0 0 7px #18E0A8; }
/* Header budget on narrow phones (design §6): under ~360px the badge is
   icon-only — 🏆 (active) / ✓ (complete) — so it fits beside the phase chip. */
@media (max-width: 360px) {
  .gm-ch-badge-txt { display:none; }
  .gm-ch-badge { padding:0 10px; }
}
.gm-ch-sheet-title { font-size:15px; letter-spacing:.4px; }
.gm-ch-rows { background:#1c1c1c; border:1px solid #2a2a2a; border-radius:14px; padding:2px 15px;
  margin-bottom:12px; text-align:left; }
.gm-ch-row { display:flex; align-items:center; justify-content:space-between; padding:13px 0; }
.gm-ch-row + .gm-ch-row { border-top:1px solid #262626; }
.gm-ch-row span { font-size:11px; font-weight:800; letter-spacing:.4px; color:#8a8a8a; }
.gm-ch-row b { font-size:14px; font-weight:800; color:#fff; }
.gm-ch-row b.teal { color:#18E0A8; }
.gm-ch-invite { background:#101010; border:1px solid #262626; border-radius:14px; padding:12px 14px;
  display:flex; align-items:center; gap:12px; margin-bottom:14px; }
.gm-ch-invite-code { flex:1; min-width:0; text-align:left; }
.gm-ch-invite-code span { display:block; font-size:9.5px; font-weight:800; letter-spacing:.6px;
  color:#8a97a0; }
.gm-ch-invite-code b { display:block; font-size:18px; font-weight:900; letter-spacing:3px;
  color:#fff; margin-top:2px; }
.gm-ch-share { padding:9px 16px; border-radius:20px; background:rgba(24,224,168,.14);
  border:1px solid rgba(24,224,168,.4); color:#18E0A8; font-size:12.5px; font-weight:800;
  cursor:pointer; flex:none; font-family:inherit; }
.gm-ch-gotit { width:100%; padding:15px; border-radius:26px; background:rgba(24,224,168,.1);
  border:1px solid rgba(24,224,168,.3); color:#18E0A8; font-size:15px; font-weight:800;
  cursor:pointer; font-family:Montserrat,system-ui,sans-serif; }
.gm-nav { position:fixed; left:0; right:0; bottom:0; z-index:1000;
  display:flex; justify-content:space-around; align-items:center;
  background:#151515; border-top:1px solid #232323; padding:8px 0 max(8px, env(safe-area-inset-bottom));
  backdrop-filter: blur(8px); }
.gm-nav-item { display:flex; flex-direction:column; align-items:center; gap:3px;
  color:#8a8a8a; text-decoration:none; font-size:10px; font-weight:700; flex:1; padding:2px 0; }
.gm-nav-item:hover { color:#cfe0ec; text-decoration:none; }
.gm-nav-item.active { color:#fff; }
.gm-nav-item.active svg { color:#18E0A8; }
`;

export default GmShell;
